import React, { useEffect, useState, useCallback } from "react";
import apiClient from "../../../services/apiClient";
import { getHeaders } from "../../ApiHeaders/apiHeaders";
import { datadogRum } from "@datadog/browser-rum";
import Dropdown from "../DropDown";

interface ParticipantsDropdownProps {
  selectedParticipant: string;
  onParticipantChange: (participant: { id: string; name: string }) => void;
  disabled?: boolean;
  className?: string;
  onFocus?: () => void;
}

interface Participant {
  participant_name: string;
  participant_id: string;
}

const ParticipantsDropdown: React.FC<ParticipantsDropdownProps> = ({
  selectedParticipant,
  onParticipantChange,
  disabled = false,
  className = "",
  onFocus,
}) => {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchParticipants = useCallback(async () => {
    setLoading(true);
    setError(null);
    const startTime = performance.now();

    try {
      const headers = await getHeaders();
      const response = await apiClient.get("/participant/", { headers });

      if (response.data?.response?.data) {
        setParticipants(response.data.response.data);
        datadogRum.addAction("Fetched Participants Successfully", {
          count: response.data.response.data.length,
        });
      } else {
        throw new Error("Invalid response structure");
      }
    } catch (err) {
      setError("Failed to fetch participants. Please try again.");
      console.error("Error fetching participants:", err);
      datadogRum.addError("Participants API Failed", { error: err });
    } finally {
      setLoading(false);
      const endTime = performance.now();
      datadogRum.addAction("Participants API Response Time", {
        duration: endTime - startTime,
      });
    }
  }, []);

  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants]);

  const uniqueParticipants = Array.from(
    new Map(participants.map((p) => [p.participant_id, p])).values()
  );

  const options = uniqueParticipants.map((p) => ({
    value: p.participant_name,
    label: p.participant_name,
  }));

  const handleChange = (value: string) => {
    const selected = participants.find((p) => p.participant_name === value);
    if (selected) {
      onParticipantChange({
        id: selected.participant_id,
        name: selected.participant_name,
      });
      datadogRum.addAction("Participant Selected", {
        participantId: selected.participant_id,
        participantName: selected.participant_name,
      });
    }
  };

  return (
    <Dropdown
      options={options}
      selectedValue={selectedParticipant}
      onValueChange={handleChange}
      disabled={disabled || loading || !!error}
      className={className}
      placeholder={
        loading ? "Loading participants..." : error ?? "Select Participant"
      }
      onFocus={onFocus}
    />
  );
};

export default ParticipantsDropdown;
